import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PaymentReminderModal.css';

const PaymentReminderModal = ({ isOpen, onClose, onSend, phoneNumber, contact }) => {
  const [nombreCliente, setNombreCliente] = useState('');
  const [idCliente, setIdCliente] = useState('');
  const [deudaCliente, setDeudaCliente] = useState('');
  const [corte, setCorte] = useState('');
  const [templateName, setTemplateName] = useState('recordatorio_de_pago'); // Nombre por defecto, puede cambiarse
  const [templateLanguage, setTemplateLanguage] = useState('es');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);

  // Función para extraer el ID de Mikrowisp del contacto
  const getMikrowispId = (contact) => {
    // Primero buscar en el campo directo mikrowispId
    if (contact?.mikrowispId) {
      return contact.mikrowispId.toString();
    }
    // Si no está, buscar en las notas como fallback
    if (contact?.notes) {
      const match = contact.notes.match(/Mikrowisp ID: (\d+)/);
      if (match) {
        return match[1];
      }
    }
    return null;
  };

  // Formatear fecha de vencimiento a formato legible
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 
                     'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
      return `${date.getDate()} de ${months[date.getMonth()]} de ${date.getFullYear()}`;
    } catch (e) {
      return dateString;
    }
  };

  // Cargar datos del cliente desde Mikrowisp y plantillas
  useEffect(() => {
    if (isOpen && contact) {
      loadClientData();
      loadTemplates();
    }
  }, [isOpen, contact]);

  // Cargar plantillas disponibles
  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await axios.get('/api/messages/templates');
      
      if (response.data && response.data.success === true) {
        const loadedTemplates = response.data.templates || [];
        setTemplates(loadedTemplates);
        
        // Buscar plantilla que contenga "recordatorio" o "pago" en el nombre
        // Priorizar "recordatorio_de_pago1" si existe, luego otras variantes
        const recordatorioTemplate1 = loadedTemplates.find(t => 
          t.status === 'APPROVED' && 
          t.name.toLowerCase() === 'recordatorio_de_pago1'
        );
        
        const recordatorioTemplate = recordatorioTemplate1 || loadedTemplates.find(t => 
          t.status === 'APPROVED' && 
          (t.name.toLowerCase().includes('recordatorio') || 
           t.name.toLowerCase().includes('pago') ||
           t.name.toLowerCase().includes('payment') ||
           t.name.toLowerCase().includes('reminder'))
        );
        
        if (recordatorioTemplate) {
          setTemplateName(recordatorioTemplate.name);
          setTemplateLanguage(recordatorioTemplate.language || 'es');
          setSelectedTemplate(recordatorioTemplate);
          console.log(`✅ Plantilla seleccionada: ${recordatorioTemplate.name} (${recordatorioTemplate.language})`);
          
          // Log de la estructura de la plantilla para debugging
          const bodyComponent = recordatorioTemplate.components?.find(c => c.type === 'BODY');
          if (bodyComponent) {
            console.log('📋 Estructura del BODY:', {
              text: bodyComponent.text,
              example: bodyComponent.example,
              format: bodyComponent.format
            });
          }
        }
      }
    } catch (error) {
      console.error('Error cargando plantillas:', error);
      // No mostrar error, solo permitir entrada manual
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadClientData = async () => {
    const mikrowispId = getMikrowispId(contact);
    if (!mikrowispId) {
      // Si no hay ID de Mikrowisp, usar datos básicos del contacto
      setNombreCliente(contact?.name || contact?.profileName || '');
      setIdCliente('');
      setDeudaCliente('');
      setCorte('');
      setError('Este contacto no tiene ID de Mikrowisp. Los datos no se pueden cargar automáticamente.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Obtener datos del cliente
      const clientResponse = await axios.post('/api/contacts/get-mikrowisp-client', {
        id: parseInt(mikrowispId)
      });

      if (clientResponse.data.success && clientResponse.data.client) {
        const clientData = clientResponse.data.client;
        
        // Llenar datos básicos
        setNombreCliente(clientData.nombre || contact?.name || contact?.profileName || '');
        setIdCliente(clientData.id?.toString() || mikrowispId);
        
        // Obtener deuda total
        const deuda = parseFloat(clientData.facturacion?.total_facturas || 0);
        setDeudaCliente(deuda > 0 ? `$${deuda.toFixed(2)}` : '$0.00');

        // Obtener facturas para calcular fecha de corte
        try {
          const invoicesResponse = await axios.post('/api/contacts/get-client-invoices', {
            idcliente: parseInt(mikrowispId)
          });

          if (invoicesResponse.data.success && invoicesResponse.data.invoices?.length > 0) {
            // Encontrar la fecha de vencimiento más próxima
            const invoices = invoicesResponse.data.invoices;
            const datesWithVencimiento = invoices
              .filter(inv => inv.vencimiento)
              .map(inv => new Date(inv.vencimiento))
              .filter(date => !isNaN(date.getTime()))
              .sort((a, b) => a - b); // Ordenar de más antigua a más nueva

            if (datesWithVencimiento.length > 0) {
              // Usar la fecha más próxima (la primera en orden cronológico)
              const fechaCorte = formatDate(datesWithVencimiento[0]);
              setCorte(fechaCorte);
            } else {
              // Si no hay fechas de vencimiento, usar fecha actual + 7 días como estimación
              const fechaEstimada = new Date();
              fechaEstimada.setDate(fechaEstimada.getDate() + 7);
              setCorte(formatDate(fechaEstimada));
            }
          } else {
            // Si no hay facturas, usar fecha actual + 7 días como estimación
            const fechaEstimada = new Date();
            fechaEstimada.setDate(fechaEstimada.getDate() + 7);
            setCorte(formatDate(fechaEstimada));
          }
        } catch (invoiceError) {
          console.error('Error obteniendo facturas:', invoiceError);
          // Si falla obtener facturas, usar fecha actual + 7 días como estimación
          const fechaEstimada = new Date();
          fechaEstimada.setDate(fechaEstimada.getDate() + 7);
          setCorte(formatDate(fechaEstimada));
        }
      } else {
        setError('No se pudo obtener la información del cliente desde Mikrowisp');
      }
    } catch (error) {
      console.error('Error cargando datos del cliente:', error);
      const errorMsg = error.response?.data?.error || 'Error al cargar información del cliente';
      setError(errorMsg);
      
      // Usar datos básicos del contacto como fallback
      setNombreCliente(contact?.name || contact?.profileName || '');
      setIdCliente(mikrowispId);
      setDeudaCliente('');
      setCorte('');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!phoneNumber) {
      alert('Por favor selecciona un contacto');
      return;
    }

    if (!nombreCliente.trim()) {
      alert('Por favor ingresa el nombre del cliente');
      return;
    }

    if (!idCliente.trim()) {
      alert('Por favor ingresa el ID del cliente');
      return;
    }

    if (!deudaCliente.trim()) {
      alert('Por favor ingresa el total a pagar');
      return;
    }

    if (!corte.trim()) {
      alert('Por favor ingresa la fecha límite de corte');
      return;
    }

    if (!templateName.trim()) {
      alert('Por favor ingresa el nombre de la plantilla');
      return;
    }

    try {
      setSending(true);

      // Construir los componentes de la plantilla según la estructura real
      const components = [];
      
      // Si no hay plantilla seleccionada pero hay nombre, intentar obtenerla
      let templateToUse = selectedTemplate;
      if (!templateToUse && templateName) {
        try {
          const templateResponse = await axios.get('/api/messages/templates');
          if (templateResponse.data && templateResponse.data.success) {
            const foundTemplate = templateResponse.data.templates.find(t => 
              t.name === templateName && t.status === 'APPROVED'
            );
            if (foundTemplate) {
              templateToUse = foundTemplate;
              setSelectedTemplate(foundTemplate);
              console.log(`✅ Plantilla "${templateName}" encontrada y seleccionada`);
            }
          }
        } catch (templateError) {
          console.warn('⚠️ No se pudo obtener la plantilla, continuando con datos disponibles');
        }
      }
      
      // Buscar el componente BODY de la plantilla seleccionada
      if (templateToUse && templateToUse.components) {
        const bodyComponent = templateToUse.components.find(c => c.type === 'BODY');
        
        if (bodyComponent) {
          // Contar cuántos parámetros tiene la plantilla
          // Método más robusto: contar todos los placeholders en el texto
          let paramCount = 0;
          
          // Primero intentar desde el ejemplo (más confiable)
          if (bodyComponent.example?.body_text && Array.isArray(bodyComponent.example.body_text)) {
            paramCount = bodyComponent.example.body_text.length;
            console.log(`📊 Parámetros detectados desde body_text: ${paramCount}`);
          } else if (bodyComponent.example?.body_text_named_params && Array.isArray(bodyComponent.example.body_text_named_params)) {
            paramCount = bodyComponent.example.body_text_named_params.length;
            console.log(`📊 Parámetros detectados desde body_text_named_params: ${paramCount}`);
          } else if (bodyComponent.text) {
            // Contar TODOS los placeholders en el texto ({{1}}, {{nombre}}, etc.)
            // Usar regex más completo que capture cualquier placeholder
            const matches = bodyComponent.text.match(/\{\{[^}]+\}\}/g);
            if (matches) {
              paramCount = matches.length;
              console.log(`📊 Parámetros detectados desde texto (${matches.length}):`, matches);
            } else {
              // Si no hay placeholders, la plantilla no tiene parámetros
              paramCount = 0;
              console.log('📊 No se encontraron parámetros en el texto de la plantilla');
            }
          }
          
          // Si aún no tenemos un conteo, intentar desde el formato de la plantilla
          if (paramCount === 0 && bodyComponent.format) {
            // Algunas plantillas tienen el formato especificado
            if (bodyComponent.format === 'TEXT' && bodyComponent.text) {
              const matches = bodyComponent.text.match(/\{\{[^}]+\}\}/g);
              if (matches) {
                paramCount = matches.length;
                console.log(`📊 Parámetros detectados desde formato TEXT: ${paramCount}`);
              }
            }
          }
          
          // Validación específica para recordatorio_de_pago1: debe tener exactamente 4 parámetros
          if (templateToUse.name === 'recordatorio_de_pago1' && paramCount !== 4) {
            console.warn(`⚠️ La plantilla recordatorio_de_pago1 debería tener 4 parámetros, pero se detectaron ${paramCount}`);
            console.warn(`   Forzando a 4 parámetros basado en la estructura conocida`);
            paramCount = 4; // Forzar a 4 parámetros para esta plantilla específica
          }
          
          // Extraer el orden exacto de los parámetros del texto de la plantilla
          // La plantilla recordatorio_de_pago1 tiene: {{1}}={{nombre}}, {{2}}={{ID}}, {{3}}={{total}}, {{4}}={{fecha}}
          let paramOrder = [];
          if (bodyComponent.text) {
            // Capturar TODOS los placeholders: {{1}}, {{2}}, {{3}}, {{4}}, etc.
            const matches = bodyComponent.text.match(/\{\{([^}]+)\}\}/g);
            if (matches) {
              paramOrder = matches.map(m => m.replace(/[{}]/g, '').trim());
              console.log(`📋 Orden de parámetros extraído del texto:`, paramOrder);
            }
          }
          
          // Construir los parámetros según la cantidad y orden que espera la plantilla
          const parameters = [];
          
          console.log(`📊 Construyendo parámetros: paramCount=${paramCount}, paramOrder.length=${paramOrder.length}`);
          
          // Si tenemos el orden del texto, usarlo; si no, usar el orden por defecto
          if (paramOrder.length > 0) {
            // Mapear los parámetros según el orden en el texto
            // Los placeholders son numéricos: {{1}}, {{2}}, {{3}}, {{4}}
            for (const paramName of paramOrder) {
              let paramValue = '';
              
              // Mapear según el número del parámetro ({{1}}, {{2}}, {{3}}, {{4}})
              const paramNum = parseInt(paramName);
              if (paramNum === 1 || paramName === '1' || paramName.toLowerCase() === 'nombre_cliente' || paramName.toLowerCase() === 'nombre') {
                paramValue = nombreCliente.trim();
              } else if (paramNum === 2 || paramName === '2' || paramName.toLowerCase() === 'id_cliente' || paramName.toLowerCase() === 'id') {
                paramValue = idCliente.trim();
              } else if (paramNum === 3 || paramName === '3' || paramName.toLowerCase() === 'deuda_cliente' || paramName.toLowerCase() === 'deuda' || paramName.toLowerCase() === 'total') {
                paramValue = deudaCliente.trim();
              } else if (paramNum === 4 || paramName === '4' || paramName.toLowerCase() === 'corte' || paramName.toLowerCase() === 'fecha') {
                paramValue = corte.trim();
              } else {
                // Si no coincide, usar valor vacío
                paramValue = '';
                console.warn(`⚠️ Parámetro desconocido en plantilla: "${paramName}", usando valor vacío`);
              }
              
              parameters.push({
                type: 'text',
                text: paramValue
              });
            }
          } else {
            // Orden por defecto si no pudimos extraer el orden
            // Para recordatorio_de_pago1: {{1}}=nombre, {{2}}=ID, {{3}}=total, {{4}}=fecha
            console.log(`📋 Usando orden por defecto para ${paramCount} parámetros`);
            if (paramCount >= 1) {
              parameters.push({ type: 'text', text: nombreCliente.trim() });
            }
            if (paramCount >= 2) {
              parameters.push({ type: 'text', text: idCliente.trim() });
            }
            if (paramCount >= 3) {
              parameters.push({ type: 'text', text: deudaCliente.trim() });
            }
            if (paramCount >= 4) {
              parameters.push({ type: 'text', text: corte.trim() });
            }
          }
          
          // CRÍTICO: Asegurar que tenemos exactamente el número de parámetros esperado
          // Si tenemos menos, agregar vacíos; si tenemos más, truncar
          if (paramCount > 0) {
            while (parameters.length < paramCount) {
              parameters.push({ type: 'text', text: '' });
              console.log(`⚠️ Agregando parámetro vacío (${parameters.length}/${paramCount})`);
            }
            
            if (parameters.length > paramCount) {
              console.log(`⚠️ Truncando parámetros de ${parameters.length} a ${paramCount}`);
              parameters.splice(paramCount);
            }
            
            console.log(`✅ Parámetros finales: ${parameters.length} (esperados: ${paramCount})`);
            
            components.push({
              type: 'body',
              parameters: parameters
            });
          } else if (paramCount === 0) {
            // Si la plantilla no tiene parámetros, no enviar componentes
            console.log('⚠️ La plantilla no tiene parámetros en el body, enviando sin componentes');
          } else {
            // Fallback: si paramCount es negativo o inválido, usar orden por defecto (4 parámetros)
            console.warn(`⚠️ paramCount inválido (${paramCount}), usando orden por defecto con 4 parámetros`);
            components.push({
              type: 'body',
              parameters: [
                { type: 'text', text: nombreCliente.trim() },
                { type: 'text', text: idCliente.trim() },
                { type: 'text', text: deudaCliente.trim() },
                { type: 'text', text: corte.trim() }
              ]
            });
          }
        } else {
          // Si no encontramos el componente BODY, usar el orden por defecto
          // Para recordatorio_de_pago1: siempre 4 parámetros en orden {{1}}, {{2}}, {{3}}, {{4}}
          const defaultParamCount = (templateToUse && templateToUse.name === 'recordatorio_de_pago1') ? 4 : 4;
          console.log(`⚠️ No se encontró componente BODY, usando orden por defecto con ${defaultParamCount} parámetros`);
          components.push({
            type: 'body',
            parameters: [
              { type: 'text', text: nombreCliente.trim() },
              { type: 'text', text: idCliente.trim() },
              { type: 'text', text: deudaCliente.trim() },
              { type: 'text', text: corte.trim() }
            ]
          });
        }
      } else {
        // Si no hay plantilla seleccionada, usar el orden por defecto
        // Para recordatorio_de_pago1: siempre 4 parámetros
        const defaultParamCount = (templateName === 'recordatorio_de_pago1') ? 4 : 4;
        console.log(`⚠️ No hay plantilla seleccionada, usando orden por defecto con ${defaultParamCount} parámetros`);
        components.push({
          type: 'body',
          parameters: [
            { type: 'text', text: nombreCliente.trim() },
            { type: 'text', text: idCliente.trim() },
            { type: 'text', text: deudaCliente.trim() },
            { type: 'text', text: corte.trim() }
          ]
        });
      }
      
      // Validación final: para recordatorio_de_pago1, asegurar exactamente 4 parámetros
      if (templateName === 'recordatorio_de_pago1') {
        const bodyComp = components.find(c => c.type === 'body');
        if (bodyComp && bodyComp.parameters) {
          if (bodyComp.parameters.length !== 4) {
            console.warn(`⚠️ recordatorio_de_pago1 requiere exactamente 4 parámetros, ajustando de ${bodyComp.parameters.length} a 4`);
            // Ajustar a exactamente 4 parámetros
            while (bodyComp.parameters.length < 4) {
              bodyComp.parameters.push({ type: 'text', text: '' });
            }
            if (bodyComp.parameters.length > 4) {
              bodyComp.parameters.splice(4);
            }
            // Asegurar que los valores estén en el orden correcto
            bodyComp.parameters[0] = { type: 'text', text: nombreCliente.trim() };
            bodyComp.parameters[1] = { type: 'text', text: idCliente.trim() };
            bodyComp.parameters[2] = { type: 'text', text: deudaCliente.trim() };
            bodyComp.parameters[3] = { type: 'text', text: corte.trim() };
          }
        } else {
          // Si no hay componente body, crearlo con 4 parámetros
          console.warn(`⚠️ No se encontró componente body para recordatorio_de_pago1, creando con 4 parámetros`);
          components.push({
            type: 'body',
            parameters: [
              { type: 'text', text: nombreCliente.trim() },
              { type: 'text', text: idCliente.trim() },
              { type: 'text', text: deudaCliente.trim() },
              { type: 'text', text: corte.trim() }
            ]
          });
        }
      }
      
      // Log para debugging
      console.log('📤 Enviando plantilla con componentes:', {
        templateName,
        templateLanguage,
        components: JSON.stringify(components, null, 2)
      });
      
      // Log detallado de parámetros
      const bodyComp = components.find(c => c.type === 'body');
      if (bodyComp && bodyComp.parameters) {
        console.log(`📊 Total de parámetros a enviar: ${bodyComp.parameters.length}`);
        bodyComp.parameters.forEach((param, index) => {
          console.log(`   ${index + 1}. "${param.text}"`);
        });
        
        // Validación final antes de enviar
        if (templateName === 'recordatorio_de_pago1' && bodyComp.parameters.length !== 4) {
          const errorMsg = `ERROR: La plantilla recordatorio_de_pago1 requiere exactamente 4 parámetros, pero se están enviando ${bodyComp.parameters.length}`;
          console.error(`❌ ${errorMsg}`);
          alert(errorMsg);
          setSending(false);
          return;
        }
      }

      await onSend(templateName.trim(), templateLanguage, components);
      onClose();
      
      // No limpiar el formulario, mantener los datos cargados
    } catch (error) {
      const errorMsg = error.response?.data?.error?.helpfulMessage || 
                      error.response?.data?.error?.message || 
                      error.message;
      alert('Error al enviar recordatorio de pago: ' + errorMsg);
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="payment-reminder-modal-overlay" onClick={onClose}>
      <div className="payment-reminder-modal" onClick={(e) => e.stopPropagation()}>
        <div className="payment-reminder-modal-header">
          <h2>💰 Enviar Recordatorio de Pago</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="payment-reminder-modal-content">
          {loading && (
            <div className="loading-message">
              <p>🔄 Cargando datos del cliente desde Mikrowisp...</p>
            </div>
          )}

          {error && (
            <div className="error-message">
              <p>⚠️ {error}</p>
            </div>
          )}

          <div className="form-group">
            <label>Nombre de la Plantilla *</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="recordatorio_de_pago"
                className="form-input"
                style={{ flex: 1 }}
              />
              {templates.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowTemplateSelector(!showTemplateSelector)}
                  className="select-template-button"
                  title="Seleccionar de plantillas disponibles"
                >
                  {showTemplateSelector ? '▲' : '▼'} Lista
                </button>
              )}
            </div>
            <small className="form-help">
              Ingresa el nombre exacto de tu plantilla aprobada en WhatsApp
            </small>
            
            {showTemplateSelector && templates.length > 0 && (
              <div className="templates-dropdown" style={{
                marginTop: '8px',
                border: '1px solid #e9edef',
                borderRadius: '8px',
                maxHeight: '300px',
                overflowY: 'auto',
                backgroundColor: 'white'
              }}>
                <div style={{ padding: '8px 12px', backgroundColor: '#f0f2f5', borderBottom: '1px solid #e9edef', fontSize: '12px', fontWeight: 500, color: '#667781' }}>
                  Plantillas Disponibles ({templates.length})
                </div>
                {templates.map((template) => (
                  <div
                    key={`${template.name}-${template.language || 'es'}`}
                    onClick={() => {
                      setTemplateName(template.name);
                      setTemplateLanguage(template.language || 'es');
                      setSelectedTemplate(template);
                      setShowTemplateSelector(false);
                    }}
                    style={{
                      padding: '12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f2f5',
                      backgroundColor: templateName === template.name ? '#e3f2fd' : 'white',
                      transition: 'background-color 0.2s',
                      opacity: template.status === 'APPROVED' ? 1 : 0.7
                    }}
                    onMouseEnter={(e) => {
                      if (templateName !== template.name) {
                        e.target.style.backgroundColor = '#f5f6f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (templateName !== template.name) {
                        e.target.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontWeight: 500, color: '#111b21' }}>{template.name}</div>
                      {template.status === 'APPROVED' && (
                        <span style={{ fontSize: '10px', backgroundColor: '#4caf50', color: 'white', padding: '2px 6px', borderRadius: '4px' }}>
                          ✓ Aprobada
                        </span>
                      )}
                      {template.status === 'PENDING' && (
                        <span style={{ fontSize: '10px', backgroundColor: '#ff9800', color: 'white', padding: '2px 6px', borderRadius: '4px' }}>
                          ⏳ Pendiente
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#667781', marginTop: '4px' }}>
                      Idioma: <strong>{template.language || 'es'}</strong> | Estado: {template.status}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {loadingTemplates && (
              <small className="form-help" style={{ color: '#1976d2' }}>
                🔄 Cargando plantillas disponibles...
              </small>
            )}
            
            {!loadingTemplates && templates.length === 0 && (
              <small className="form-help" style={{ color: '#f44336' }}>
                ⚠️ No se pudieron cargar las plantillas. Ingresa el nombre manualmente.
              </small>
            )}
          </div>

          <div className="form-group">
            <label>Idioma de la Plantilla</label>
            <select
              value={templateLanguage}
              onChange={(e) => setTemplateLanguage(e.target.value)}
              className="form-input"
            >
              <option value="es">Español (es)</option>
              <option value="en">Inglés (en)</option>
              <option value="en_US">Inglés US (en_US)</option>
              <option value="es_MX">Español México (es_MX)</option>
              <option value="es_ES">Español España (es_ES)</option>
            </select>
            <small className="form-help">
              Selecciona el código de idioma exacto de tu plantilla
            </small>
          </div>

          <div className="form-group">
            <label>Nombre del Cliente *</label>
            <input
              type="text"
              value={nombreCliente}
              onChange={(e) => setNombreCliente(e.target.value)}
              placeholder="Ej: Juan Pérez"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>ID Cliente *</label>
            <input
              type="text"
              value={idCliente}
              onChange={(e) => setIdCliente(e.target.value)}
              placeholder="Ej: 12345"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>Total a Pagar *</label>
            <input
              type="text"
              value={deudaCliente}
              onChange={(e) => setDeudaCliente(e.target.value)}
              placeholder="Ej: $1,500.00"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>Fecha Límite de Corte *</label>
            <input
              type="text"
              value={corte}
              onChange={(e) => setCorte(e.target.value)}
              placeholder="Ej: 15 de noviembre de 2025"
              className="form-input"
            />
            <small className="form-help">
              Formato: día de mes de año (Ej: 15 de noviembre de 2025)
            </small>
          </div>

          {selectedTemplate && selectedTemplate.components && (
            <div className="template-structure-info" style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#f0f2f5',
              borderRadius: '8px',
              fontSize: '13px'
            }}>
              <strong>📋 Estructura de la Plantilla:</strong>
              {selectedTemplate.components.map((comp, idx) => {
                if (comp.type === 'BODY') {
                  let paramCount = 0;
                  let paramFormat = 'numérico';
                  
                  if (comp.example?.body_text) {
                    paramCount = comp.example.body_text.length;
                    paramFormat = 'numérico ({{1}}, {{2}}, etc.)';
                  } else if (comp.example?.body_text_named_params) {
                    paramCount = comp.example.body_text_named_params.length;
                    paramFormat = 'nombrado ({{nombre_cliente}}, etc.)';
                  } else if (comp.text) {
                    const matches = comp.text.match(/\{\{([^}]+)\}\}/g);
                    if (matches) {
                      paramCount = matches.length;
                      const firstParam = matches[0].replace(/[{}]/g, '');
                      paramFormat = isNaN(firstParam) ? 'nombrado' : 'numérico';
                    }
                  }
                  
                  return (
                    <div key={idx} style={{ marginTop: '8px' }}>
                      <div>Parámetros en el cuerpo: <strong>{paramCount}</strong> (formato: {paramFormat})</div>
                      {paramCount > 0 && (
                        <div style={{ marginTop: '4px', fontSize: '12px', color: '#667781' }}>
                          La plantilla espera {paramCount} parámetro(s) en este orden:
                          <ol style={{ margin: '4px 0 0 20px', padding: 0 }}>
                            <li>Nombre del cliente</li>
                            {paramCount >= 2 && <li>ID del cliente</li>}
                            {paramCount >= 3 && <li>Total a pagar</li>}
                            {paramCount >= 4 && <li>Fecha límite</li>}
                          </ol>
                          {selectedTemplate.parameter_format === 'NAMED' && (
                            <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fff3cd', borderRadius: '4px', fontSize: '11px', color: '#856404' }}>
                              <span>⚠️</span> <strong>Nota:</strong> Esta plantilla usa parámetros nombrados. Si encuentras errores al enviar, considera recrear la plantilla con parámetros numéricos (ejemplo: número 1, número 2, etc.) que es el formato recomendado por WhatsApp.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })}
            </div>
          )}

          <div className="template-preview">
            <h3>Vista Previa del Mensaje:</h3>
            <div className="preview-content">
              <p><strong>Recordatorio de Pago</strong></p>
              <p>Estimado, <strong>{nombreCliente || '{{nombre_cliente}}'}</strong>.</p>
              <p>ID cliente: <strong>{idCliente || '{{id_cliente}}'}</strong>.</p>
              <p>Recordamos el pago de su servicio de INTERNET</p>
              <p>Total a Pagar <strong>{deudaCliente || '{{deuda_cliente}}'}</strong></p>
              <p>Su fecha limite es <strong>{corte || '{{corte}}'}</strong></p>
              <p>Mas informacion</p>
              <p>👇👇👇</p>
              <p>14:57</p>
              <p>Factura</p>
              <p>Cuenta para Pago</p>
              <p>Ubicacion Punto de Pago</p>
            </div>
          </div>
        </div>

        <div className="payment-reminder-modal-actions">
          <button onClick={onClose} className="cancel-button">
            Cancelar
          </button>
          <button
            onClick={handleSend}
            disabled={sending || loading || !phoneNumber || !nombreCliente.trim() || !idCliente.trim() || !deudaCliente.trim() || !corte.trim()}
            className="send-button"
          >
            {sending ? 'Enviando...' : loading ? 'Cargando...' : '💰 Enviar Recordatorio'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentReminderModal;

